/**
 * Connected Self Balance Framework
 * 
 * The Connected Self philosophy: What impacts one level impacts all.
 * True healing requires balance across all five dimensions of self.
 * 
 * Order follows QSP framework: Physical, Emotional, Mental, Spiritual, Relational
 * 
 * Each dimension has a spectrum - too much of either side creates imbalance.
 * The goal is not perfection but awareness and adaptive adjustment.
 */

export interface BalanceDimension {
  id: string;
  name: string;
  leftPole: string;
  rightPole: string;
  leftIcon: string;
  rightIcon: string;
  colour: string; // Brand colour class
  description: string;
  leftExcessSymptoms: string[];
  rightExcessSymptoms: string[];
  leftExcessInsight: string;
  rightExcessInsight: string;
  balancedState: string;
}

export interface BalanceScore {
  dimensionId: string;
  score: number; // -100 to +100, 0 = balanced, negative = left pole excess, positive = right pole excess
  tilt: 'left' | 'balanced' | 'right';
  severity: 'mild' | 'moderate' | 'significant';
  insight: string;
}

export interface BalanceProfile {
  scores: BalanceScore[];
  overallBalance: number; // 0-100, 100 = perfectly balanced
  primaryImbalance: string | null;
  recommendations: string[];
  timestamp: Date;
}

// The Five Balance Dimensions - QSP Order: Physical, Emotional, Mental, Spiritual, Relational
export const BALANCE_DIMENSIONS: BalanceDimension[] = [
  {
    id: 'physical',
    name: 'Physical',
    leftPole: 'Activity',
    rightPole: 'Rest',
    leftIcon: 'activity',
    rightIcon: 'bed',
    colour: 'from-emerald-500 to-teal-500',
    description: 'Your body needs both movement and recovery. Too much activity depletes; too much rest stagnates.',
    leftExcessSymptoms: [
      'Chronic fatigue or exhaustion',
      'Frequent injuries or pain',
      'Irritability and short temper',
      'Sleep disturbances despite tiredness',
      'Weakened immune system',
      'Adrenal fatigue signs'
    ],
    rightExcessSymptoms: [
      'Low energy and lethargy',
      'Weight changes',
      'Restlessness despite not moving',
      'Muscle stiffness',
      'Brain fog',
      'Feeling "stuck" in life'
    ],
    leftExcessInsight: 'Your nervous system may be signalling a need for recovery. Consider whether you\'re using activity to avoid stillness, or if your sense of worth is tied to productivity.',
    rightExcessInsight: 'Gentle movement can actually restore energy rather than deplete it. Sometimes the body craves rest when the mind needs stimulation - consider what you might be avoiding.',
    balancedState: 'When balanced, you feel energised by movement and restored by rest. Your body communicates clearly what it needs.'
  },
  {
    id: 'emotional',
    name: 'Emotional',
    leftPole: 'Expression',
    rightPole: 'Processing',
    leftIcon: 'message-circle',
    rightIcon: 'cloud',
    colour: 'from-rose-500 to-pink-500',
    description: 'Emotions need both expression and internal processing. Either extreme disrupts emotional health.',
    leftExcessSymptoms: [
      'Emotional flooding or volatility',
      'Drama in relationships',
      'Difficulty self-regulating',
      'Exhausting others with emotions',
      'Feeling out of control',
      'Shame after emotional outbursts'
    ],
    rightExcessSymptoms: [
      'Emotional numbness',
      'Physical symptoms (headaches, tension)',
      'Difficulty identifying feelings',
      'Unexpected emotional eruptions',
      'Feeling disconnected from self',
      'Difficulty with intimacy'
    ],
    leftExcessInsight: 'Expressing everything may be preventing you from truly processing. Consider whether you\'re seeking validation rather than resolution.',
    rightExcessInsight: 'Unexpressed emotions don\'t disappear - they manifest elsewhere. What feels unsafe about letting others see your inner world?',
    balancedState: 'When balanced, you feel your emotions fully, express them appropriately, and process them completely. Emotions inform rather than overwhelm.'
  },
  {
    id: 'mental',
    name: 'Mental',
    leftPole: 'Stimulation',
    rightPole: 'Relaxation',
    leftIcon: 'brain',
    rightIcon: 'flower',
    colour: 'from-blue-500 to-indigo-500',
    description: 'The mind needs engagement and quiet. Too much stimulation overwhelms; too little leads to stagnation.',
    leftExcessSymptoms: [
      'Racing thoughts and anxiety',
      'Difficulty sleeping (mind won\'t stop)',
      'Burnout and mental exhaustion',
      'Inability to be present',
      'Decision fatigue',
      'Feeling overwhelmed by small tasks'
    ],
    rightExcessSymptoms: [
      'Brain fog and poor concentration',
      'Lack of motivation or purpose',
      'Feeling disconnected from life',
      'Depressive tendencies',
      'Boredom despite having options',
      'Loss of interest in things once enjoyed'
    ],
    leftExcessInsight: 'Your mind may be using constant stimulation as a protective mechanism. What thoughts or feelings might you be avoiding by staying busy?',
    rightExcessInsight: 'Mental engagement can reignite purpose. Sometimes the mind craves rest when what it actually needs is meaningful challenge.',
    balancedState: 'When balanced, you can focus deeply when needed and quiet your mind at will. Thoughts serve you rather than control you.'
  },
  {
    id: 'spiritual',
    name: 'Spiritual',
    leftPole: 'Seeking',
    rightPole: 'Being',
    leftIcon: 'compass',
    rightIcon: 'circle',
    colour: 'from-purple-500 to-violet-500',
    description: 'Spiritual health balances active seeking with present-moment being. Either extreme creates disconnection.',
    leftExcessSymptoms: [
      'Spiritual bypassing',
      'Dissociation from body/reality',
      'Constant searching, never arriving',
      'Using spirituality to avoid life',
      'Feeling superior or separate',
      'Difficulty with practical matters'
    ],
    rightExcessSymptoms: [
      'Loss of meaning or purpose',
      'Existential anxiety or dread',
      'Disconnection from something greater',
      'Nihilistic tendencies',
      'Feeling life is meaningless',
      'Lack of wonder or awe'
    ],
    leftExcessInsight: 'Constant seeking may be avoiding the present moment. True spirituality often means being fully here, not elsewhere.',
    rightExcessInsight: 'Meaning isn\'t found - it\'s created through connection and engagement. Consider what practices might reconnect you to something larger.',
    balancedState: 'When balanced, you feel connected to something greater whilst fully present in daily life. Spiritual practice enhances rather than escapes reality.'
  },
  {
    id: 'relational',
    name: 'Relational',
    leftPole: 'Connection',
    rightPole: 'Solitude',
    leftIcon: 'users',
    rightIcon: 'home',
    colour: 'from-amber-500 to-orange-500',
    description: 'We need both meaningful connection and restorative solitude. Either extreme creates disconnection from self or others.',
    leftExcessSymptoms: [
      'Loss of sense of self',
      'People-pleasing tendencies',
      'Feeling overwhelmed by others\' needs',
      'Difficulty making decisions alone',
      'Resentment building in relationships',
      'Energy depletion after socialising'
    ],
    rightExcessSymptoms: [
      'Loneliness and isolation',
      'Difficulty reaching out for help',
      'Social anxiety when connecting',
      'Feeling misunderstood',
      'Loss of social skills',
      'Disconnection from community'
    ],
    leftExcessInsight: 'If you\'re giving more than you\'re receiving, you may be abandoning yourself for others. What need are you trying to meet through constant connection?',
    rightExcessInsight: 'Isolation often masquerades as self-care. Consider whether solitude is restoring you or whether you\'re avoiding the vulnerability of connection.',
    balancedState: 'When balanced, you feel nourished by both deep connections and peaceful solitude. You maintain yourself whilst being present for others.'
  }
];

/**
 * Calculate balance score for a dimension from check-in data
 * Returns -100 to +100 where 0 is balanced
 */
export function calculateDimensionBalance(
  dimensionId: string,
  checkInData: Record<string, any>,
  historicalData?: Record<string, any>[]
): number {
  switch (dimensionId) {
    case 'physical':
      return calculatePhysicalBalance(checkInData, historicalData);
    case 'emotional':
      return calculateEmotionalBalance(checkInData, historicalData);
    case 'mental':
      return calculateMentalBalance(checkInData, historicalData);
    case 'spiritual':
      return calculateSpiritualBalance(checkInData, historicalData);
    case 'relational':
      return calculateRelationalBalance(checkInData, historicalData);
    default:
      return 0;
  }
}

function calculatePhysicalBalance(data: Record<string, any>, history?: Record<string, any>[]): number {
  // Activity indicators (positive = more activity)
  const exerciseMinutes = data.exercise_minutes || 0;
  const exerciseIntensity = data.exercise_intensity || 'none';
  const stepsOrMovement = data.steps || 0;
  
  // Rest indicators
  const hoursSlept = data.hours_slept || 7;
  const sleepQuality = data.sleep_quality || 5;
  const restActivities = data.rest_activities || [];
  const fatigueLevel = data.fatigue_level || 5;
  
  // WHOOP Integration - extract if present in data
  const whoopRecovery = data.whoop_recovery_score || data.recovery_score || null;
  const whoopHrv = data.whoop_hrv || data.hrv_rmssd || null;
  const whoopStrain = data.whoop_strain || data.strain || null;
  const whoopSleepPerformance = data.whoop_sleep_performance || data.sleep_performance || null;
  
  // Calculate activity score (0-100)
  let activityScore = 0;
  if (exerciseMinutes > 0) {
    activityScore += Math.min(exerciseMinutes / 60, 1) * 40; // Up to 40 points for 60+ min
    if (exerciseIntensity === 'high') activityScore += 20;
    else if (exerciseIntensity === 'moderate') activityScore += 10;
  }
  activityScore += Math.min(stepsOrMovement / 10000, 1) * 20; // Up to 20 points for 10k steps
  
  // WHOOP: High strain indicates more activity
  if (whoopStrain !== null) {
    activityScore += Math.min((whoopStrain / 21) * 25, 25); // Up to 25 points for max strain (21)
  }
  
  // WHOOP: Low HRV can indicate overtraining/stress (body needs rest)
  if (whoopHrv !== null && whoopHrv < 40) {
    activityScore += 15; // Body is stressed, pushing toward needing more rest
  }
  
  // Calculate rest score (0-100)
  let restScore = 0;
  restScore += (hoursSlept / 9) * 40; // Up to 40 points for 9 hours
  restScore += (sleepQuality / 10) * 30; // Up to 30 points for quality
  restScore += (10 - fatigueLevel) / 10 * 30; // Up to 30 points for low fatigue
  
  // WHOOP: High recovery indicates good rest
  if (whoopRecovery !== null) {
    restScore += Math.min((whoopRecovery / 100) * 25, 25); // Up to 25 points for 100% recovery
  }
  
  // WHOOP: Good sleep performance adds to rest
  if (whoopSleepPerformance !== null) {
    restScore += Math.min((whoopSleepPerformance / 100) * 15, 15); // Up to 15 points
  }
  
  // Balance: 0 = balanced, negative = too much activity, positive = too much rest
  const balance = restScore - activityScore;
  
  // Normalise to -100 to +100 range
  return Math.max(-100, Math.min(100, balance));
}

function calculateEmotionalBalance(data: Record<string, any>, history?: Record<string, any>[]): number {
  // Expression indicators
  const journaledEmotions = data.journaled_emotions || data.journaled ? 1 : 0;
  const talkedAboutFeelings = data.talked_about_feelings ? 1 : 0;
  const emotionalOutburst = data.emotional_outburst ? 1 : 0;
  const sharedVulnerability = data.shared_vulnerability ? 1 : 0;
  
  // Processing indicators
  const emotionalAwareness = data.emotional_awareness || 5;
  const emotionScore = data.emotion_score || 5;
  const processedInternally = data.processed_internally ? 1 : 0;
  const feltNumb = data.felt_numb ? 1 : 0;
  
  // Calculate expression score
  let expressionScore = 0;
  expressionScore += journaledEmotions * 25;
  expressionScore += talkedAboutFeelings * 30;
  expressionScore += emotionalOutburst * 25; // Can indicate over-expression
  expressionScore += sharedVulnerability * 20;
  
  // Calculate processing score
  let processingScore = 0;
  processingScore += (emotionalAwareness / 10) * 40;
  processingScore += processedInternally * 30;
  processingScore += feltNumb * 30; // Indicates suppression/over-processing
  
  // Balance: negative = too much expression, positive = too much processing/suppression
  const balance = processingScore - expressionScore;
  return Math.max(-100, Math.min(100, balance));
}

function calculateMentalBalance(data: Record<string, any>, history?: Record<string, any>[]): number {
  // Stimulation indicators
  const workHours = data.work_hours || data.mental_work_hours || 0;
  const screenTime = data.screen_time_hours || 0;
  const learningTime = data.learning_minutes || 0;
  const stressLevel = data.stress_level || 5;
  const anxietyLevel = data.anxiety_level || 5;
  
  // Relaxation indicators
  const meditationMinutes = data.meditation_minutes || 0;
  const mindfulnessScore = data.mindfulness_score || 5;
  const headspaceLevel = data.headspace_level || 5;
  const mentalClarity = data.mind_clarity || 5;
  
  // Calculate stimulation score
  let stimulationScore = 0;
  stimulationScore += Math.min(workHours / 10, 1) * 30; // Up to 30 points
  stimulationScore += Math.min(screenTime / 8, 1) * 20; // Up to 20 points
  stimulationScore += (stressLevel / 10) * 25; // Up to 25 points
  stimulationScore += (anxietyLevel / 10) * 25; // Up to 25 points
  
  // Calculate relaxation score
  let relaxationScore = 0;
  relaxationScore += Math.min(meditationMinutes / 30, 1) * 30; // Up to 30 points for 30 min
  relaxationScore += (headspaceLevel / 10) * 35; // Up to 35 points
  relaxationScore += (mentalClarity / 10) * 35; // Up to 35 points
  
  // Balance: negative = too much stimulation, positive = too much relaxation
  const balance = relaxationScore - stimulationScore;
  return Math.max(-100, Math.min(100, balance));
}

function calculateSpiritualBalance(data: Record<string, any>, history?: Record<string, any>[]): number {
  // Seeking indicators
  const spiritualPracticeMinutes = data.spiritual_practice_minutes || 0;
  const seekingMeaning = data.seeking_meaning ? 1 : 0;
  const spiritualReading = data.spiritual_reading ? 1 : 0;
  const spiritualBypass = data.avoiding_problems_spiritually ? 1 : 0;
  
  // Being/grounding indicators
  const presentMomentAwareness = data.present_moment_awareness || 5;
  const spiritualConnection = data.spiritual_connection_score || data.spiritual_connection || 5;
  const gratitudeLogged = data.gratitude_logged ? 1 : 0;
  const feltGrounded = data.felt_grounded ? 1 : 0;
  
  // Normalise spiritual_connection if it's a string
  let spiritualScore = typeof spiritualConnection === 'string' 
    ? (spiritualConnection === 'Deeply Connected' ? 10 : spiritualConnection === 'Open & Receptive' ? 7 : 4)
    : spiritualConnection;
  
  // Calculate seeking score
  let seekingScore = 0;
  seekingScore += Math.min(spiritualPracticeMinutes / 60, 1) * 30;
  seekingScore += seekingMeaning * 25;
  seekingScore += spiritualReading * 25;
  seekingScore += spiritualBypass * 20; // Indicates over-seeking to avoid reality
  
  // Calculate being score
  let beingScore = 0;
  beingScore += (presentMomentAwareness / 10) * 30;
  beingScore += (spiritualScore / 10) * 30;
  beingScore += gratitudeLogged * 20;
  beingScore += feltGrounded * 20;
  
  // Balance: negative = too much seeking, positive = too much passive being
  const balance = beingScore - seekingScore;
  return Math.max(-100, Math.min(100, balance));
}

function calculateRelationalBalance(data: Record<string, any>, history?: Record<string, any>[]): number {
  // Connection indicators
  const meaningfulInteractions = data.meaningful_interactions || 0;
  const feltConnected = data.felt_connected ? 1 : 0;
  const socialActivityCount = data.social_activity_count || 0;
  const socialEnergyGain = data.social_energy_gain || 0;
  
  // Solitude indicators
  const aloneTimeHours = data.alone_time_hours || 0;
  const feltLonely = data.felt_lonely ? 1 : 0;
  const needsSpace = data.needs_space ? 1 : 0;
  const socialDrained = data.social_drained ? 1 : 0;
  
  // Calculate connection score
  let connectionScore = 0;
  connectionScore += Math.min(meaningfulInteractions / 3, 1) * 40; // Up to 40 points for 3+ interactions
  connectionScore += feltConnected * 30;
  connectionScore += Math.min(socialActivityCount / 2, 1) * 30;
  
  // Calculate solitude score
  let solitudeScore = 0;
  solitudeScore += Math.min(aloneTimeHours / 4, 1) * 40; // Up to 40 points for 4 hours alone
  solitudeScore += socialDrained * 30;
  solitudeScore += needsSpace * 30;
  
  // Adjust for loneliness (indicates need for more connection)
  if (feltLonely) {
    solitudeScore += 20;
  }
  
  // Balance: negative = too much connection, positive = too much solitude
  const balance = solitudeScore - connectionScore;
  return Math.max(-100, Math.min(100, balance));
}

/**
 * Get insight based on balance score
 */
export function getBalanceInsight(dimensionId: string, score: number): string {
  const dimension = BALANCE_DIMENSIONS.find(d => d.id === dimensionId);
  if (!dimension) return '';
  
  const absScore = Math.abs(score);
  const severity = absScore < 20 ? 'mild' : absScore < 50 ? 'moderate' : 'significant';
  
  if (absScore < 15) {
    return dimension.balancedState;
  }
  
  if (score < 0) {
    // Too much left pole
    const severityPrefix = severity === 'significant' ? 'Your data suggests significant ' : 
                          severity === 'moderate' ? 'There appears to be ' : 'There may be slight ';
    return `${severityPrefix}emphasis on ${dimension.leftPole.toLowerCase()}. ${dimension.leftExcessInsight}`;
  } else {
    // Too much right pole
    const severityPrefix = severity === 'significant' ? 'Your data suggests significant ' : 
                          severity === 'moderate' ? 'There appears to be ' : 'There may be slight ';
    return `${severityPrefix}emphasis on ${dimension.rightPole.toLowerCase()}. ${dimension.rightExcessInsight}`;
  }
}

/**
 * Calculate full balance profile from check-in data
 */
export function calculateBalanceProfile(
  checkInData: Record<string, any>,
  historicalData?: Record<string, any>[]
): BalanceProfile {
  const scores: BalanceScore[] = BALANCE_DIMENSIONS.map(dimension => {
    const score = calculateDimensionBalance(dimension.id, checkInData, historicalData);
    const absScore = Math.abs(score);
    
    return {
      dimensionId: dimension.id,
      score,
      tilt: absScore < 15 ? 'balanced' : score < 0 ? 'left' : 'right',
      severity: absScore < 20 ? 'mild' : absScore < 50 ? 'moderate' : 'significant',
      insight: getBalanceInsight(dimension.id, score)
    };
  });
  
  // Calculate overall balance (100 = perfectly balanced)
  const avgImbalance = scores.reduce((sum, s) => sum + Math.abs(s.score), 0) / scores.length;
  const overallBalance = Math.max(0, 100 - avgImbalance);
  
  // Find primary imbalance
  const worstScore = scores.reduce((worst, s) => 
    Math.abs(s.score) > Math.abs(worst.score) ? s : worst
  , scores[0]);
  
  const primaryImbalance = Math.abs(worstScore.score) > 20 ? worstScore.dimensionId : null;
  
  // Generate recommendations
  const recommendations = generateRecommendations(scores);
  
  return {
    scores,
    overallBalance,
    primaryImbalance,
    recommendations,
    timestamp: new Date()
  };
}

function generateRecommendations(scores: BalanceScore[]): string[] {
  const recommendations: string[] = [];
  
  scores.forEach(score => {
    const dimension = BALANCE_DIMENSIONS.find(d => d.id === score.dimensionId);
    if (!dimension) return;
    
    if (score.severity === 'significant' || score.severity === 'moderate') {
      if (score.tilt === 'left') {
        // Too much left pole activity
        recommendations.push(getLeftPoleRecommendation(dimension));
      } else if (score.tilt === 'right') {
        // Too much right pole activity
        recommendations.push(getRightPoleRecommendation(dimension));
      }
    }
  });
  
  // Add general interconnection reminder if multiple imbalances
  const imbalancedCount = scores.filter(s => s.severity !== 'mild' || s.tilt !== 'balanced').length;
  if (imbalancedCount >= 3) {
    recommendations.push('Remember: your dimensions are interconnected. Addressing one area often creates positive ripples across all others.');
  }
  
  return recommendations.slice(0, 4); // Limit to 4 recommendations
}

function getLeftPoleRecommendation(dimension: BalanceDimension): string {
  switch (dimension.id) {
    case 'physical':
      return 'Consider adding restorative practices like gentle stretching, warm baths, or earlier bedtimes to balance your physical activity.';
    case 'emotional':
      return 'Try journaling privately before sharing emotions with others - this can help you process and express more intentionally.';
    case 'mental':
      return 'Schedule deliberate "mental rest" periods - even 10 minutes of quiet without stimulation can help reset an overstimulated mind.';
    case 'spiritual':
      return 'Ground your spiritual practice in daily presence rather than seeking new teachings - what you need may already be within.';
    case 'relational':
      return 'Protect some time for solitude this week - being alone can help you reconnect with yourself and show up more fully for others.';
    default:
      return '';
  }
}

function getRightPoleRecommendation(dimension: BalanceDimension): string {
  switch (dimension.id) {
    case 'physical':
      return 'Gentle movement like a short walk or stretching can actually restore energy. Start small - even 5 minutes counts.';
    case 'emotional':
      return 'Consider sharing one feeling with a trusted person this week. Expression helps complete the emotional cycle.';
    case 'mental':
      return 'Engage your mind with something that genuinely interests you - learning something new can reignite motivation.';
    case 'spiritual':
      return 'A simple gratitude practice or time in nature can reconnect you to meaning without requiring intense seeking.';
    case 'relational':
      return 'Reach out to someone you trust - even a brief message can help bridge the gap between isolation and connection.';
    default:
      return '';
  }
}

/**
 * Build context string for AI conversations
 */
export function buildBalanceContextForAI(profile: BalanceProfile): string {
  const lines: string[] = [
    `Connected Self Balance Profile (${Math.round(profile.overallBalance)}% overall balance):`,
  ];
  
  profile.scores.forEach(score => {
    const dimension = BALANCE_DIMENSIONS.find(d => d.id === score.dimensionId);
    if (!dimension) return;
    
    const status = score.tilt === 'balanced' 
      ? 'balanced' 
      : `${score.severity} tilt toward ${score.tilt === 'left' ? dimension.leftPole : dimension.rightPole}`;
    
    lines.push(`- ${dimension.name}: ${status}`);
  });
  
  if (profile.primaryImbalance) {
    const primaryDimension = BALANCE_DIMENSIONS.find(d => d.id === profile.primaryImbalance);
    lines.push(`\nPrimary focus area: ${primaryDimension?.name || profile.primaryImbalance}`);
  }
  
  lines.push('\nRemember: Individual balance needs vary based on nervous system state and life circumstances. What depletes one person may energise another.');
  
  return lines.join('\n');
}
